// APG CoPilot session core — the frontend-owned Conversation aggregate + the single-flight
// Run lock + suggestion identity assignment. Pure logic (no Angular) so the invariants are
// unit-tested directly. ISOLATED SIBLING: re-implements the Hedge CoPilot's proven design
// WITHOUT importing it. ES5 note: arrays only.

import {
    Conversation,
    Run,
    RollPositionsInputs,
    RollSuggestion,
    RollSuggestionContent,
    RunOutcome,
    Tab,
    TabKind
} from './apg-copilot-domain';

// ---- Global single-flight lock: at most ONE Run in flight (one runnable tab, but the lock is
// kept for parity + defence against re-entrant Runs). ------------------------------------
export class SingleFlightLock {
    private _heldBy: string | null = null;

    get isHeld(): boolean {
        return this._heldBy !== null;
    }

    get holder(): string | null {
        return this._heldBy;
    }

    tryAcquire(runId: string): boolean {
        if (this._heldBy !== null) {
            return false;
        }
        this._heldBy = runId;
        return true;
    }

    // Release only if this run holds it (a superseded run must not release a newer holder).
    release(runId: string): void {
        if (this._heldBy === runId) {
            this._heldBy = null;
        }
    }

    releaseAny(): void {
        this._heldBy = null;
    }
}

// Build suggestions from skill-authored content, assigning each a HOST identity = "<runId>#<index>".
// Two same-content suggestions get DISTINCT ids; the content is never used as a key.
export function buildSuggestions(runId: string, contents: RollSuggestionContent[]): RollSuggestion[] {
    return contents.map((content, index) => ({
        id: runId + '#' + index,
        content,
        state: { previewed: false }
    }));
}

function emptyTab(kind: TabKind): Tab {
    return { kind, inputs: null, currentRun: null, suggestions: [] };
}

export class ApgCopilotSession {
    private readonly _lock = new SingleFlightLock();
    private _conversation: Conversation | null = null;
    private _panelInstanceId: string | null = null;

    // idFactory: client-mints Run/Conversation ids (getShortUUID); clock: epoch-ms source.
    // Both injected so the pure specs stay deterministic.
    constructor(
        private readonly _newId: () => string,
        private readonly _now: () => number
    ) {}

    // CR-20260910-001: the opaque per-PANEL-INSTANCE id the migrated run needs. The backend run
    // registry scopes supersede to (terminalId, panelInstanceId), so two APG panels on one terminal
    // never displace each other'''s runs - a real case, because the Adjustment Comparison Grid hosts
    // two inner grids side by side.
    //
    // A SESSION-LIFETIME ID IS A PANEL-INSTANCE ID here: the orchestrator creates ONE session per
    // component and never replaces it (conversations restart INSIDE it). Minted LAZILY on first
    // read, so constructing a session stays allocation-neutral for the deterministic id factories
    // the specs inject. Same shape as OpgCopilotSession.panelInstanceId, for the same reason.
    get panelInstanceId(): string {
        if (this._panelInstanceId === null) {
            this._panelInstanceId = this._newId();
        }
        return this._panelInstanceId;
    }

    get conversation(): Conversation | null {
        return this._conversation;
    }

    get isRunInFlight(): boolean {
        return this._lock.isHeld;
    }

    get inFlightRunId(): string | null {
        return this._lock.holder;
    }

    startConversation(portfolioId: string): Conversation {
        this._conversation = {
            id: this._newId(),
            portfolioId,
            tabs: { rollPositions: emptyTab('rollPositions'), zone: emptyTab('zone') }
        };
        return this._conversation;
    }

    tab(kind: TabKind): Tab | null {
        return this._conversation ? this._conversation.tabs[kind] : null;
    }

    // Start a Run on a tab. Returns null if the global lock is held (another Run in flight).
    // Otherwise supersedes this tab's prior CURRENT run, snapshots the inputs, acquires the lock.
    startRun(kind: TabKind, inputs: RollPositionsInputs): Run | null {
        const tab = this.tab(kind);
        if (!tab) {
            return null;
        }
        const runId = this._newId();
        if (!this._lock.tryAcquire(runId)) {
            return null;   // single-flight: a Run is already pending
        }
        const run: Run = {
            id: runId,
            tabKind: kind,
            sentInputs: inputs,
            outcome: 'pending',
            startedAt: this._now(),
            endedAt: null,
            failureDetail: null
        };
        tab.currentRun = run;   // supersedes any prior CURRENT run on this tab
        return run;
    }

    // Is this run still the tab's CURRENT one AND the lock holder? Late/cancelled results are discarded.
    isCurrent(runId: string): boolean {
        if (this._lock.holder !== runId) {
            return false;
        }
        const tab = this._tabOfRun(runId);
        return tab != null && tab.currentRun != null && tab.currentRun.id === runId;
    }

    // Settle the in-flight Run. Ignores results for a run that is no longer current (late/dup
    // discard). On 'succeeded' builds suggestions with host identities. Releases the global lock.
    completeRun(runId: string, outcome: RunOutcome, contents: RollSuggestionContent[], failureDetail: string | null): boolean {
        if (!this.isCurrent(runId)) {
            return false;   // discarded
        }
        const tab = this._tabOfRun(runId) as Tab;
        const run = tab.currentRun as Run;
        run.outcome = outcome;
        run.endedAt = this._now();
        run.failureDetail = failureDetail;
        tab.suggestions = outcome === 'succeeded' ? buildSuggestions(runId, contents) : [];
        this._lock.release(runId);
        return true;
    }

    // Cancel the in-flight Run (FE-side). Releases the lock so the user can immediately RUN again.
    cancelRun(runId: string): void {
        const tab = this._tabOfRun(runId);
        if (tab && tab.currentRun && tab.currentRun.id === runId) {
            tab.currentRun.outcome = 'cancelled';
            tab.currentRun.endedAt = this._now();
        }
        this._lock.release(runId);
    }

    // Re-run reset for a tab: keep the questionnaire inputs; clear suggestions + the CURRENT run.
    resetTabForReRun(kind: TabKind): void {
        const tab = this.tab(kind);
        if (!tab) {
            return;
        }
        tab.suggestions = [];
        tab.currentRun = null;
    }

    private _tabOfRun(runId: string): Tab | null {
        if (!this._conversation) {
            return null;
        }
        const tabs = this._conversation.tabs;
        const all = [tabs.rollPositions, tabs.zone];
        let match: Tab | null = null;
        all.forEach(t => {
            if (t.currentRun && t.currentRun.id === runId) {
                match = t;
            }
        });
        return match;
    }
}
